import { decode } from "@toon-format/toon";
import { isJsonValue, type JsonValue } from "@/graphql/json-value";

const FOLDED_PATH_PATTERN = /^[A-Za-z_][\w]*(?:\.[A-Za-z_][\w]*)+$/;

/**
 * keyFolding: "safe" で保存した旧履歴を、v4 で廃止された path expansion 込みで復元する。
 * 引用されたキーはリテラルとして保持し、展開先の衝突は従来の strict decode と同様に失敗させる。
 */
export function decodeLegacyToon(input: string): JsonValue {
  // v4 の decoder はキーが引用されていたかを返さないため、decode 前に区別を保存する。
  let prefix = "__amethyst_quoted_key_";
  while (input.includes(prefix)) prefix += "_";
  const quotedValues = new Map<string, string>();
  const protectedInput = input.replace(/"(?:[^"\\\n]|\\.)*"/g, (token) => {
    const value: unknown = JSON.parse(token);
    if (typeof value !== "string" || !FOLDED_PATH_PATTERN.test(value)) {
      return token;
    }
    const placeholder = `${prefix}${quotedValues.size}`;
    quotedValues.set(placeholder, value);
    return JSON.stringify(placeholder);
  });

  function expand(value: JsonValue): JsonValue {
    if (typeof value === "string") return quotedValues.get(value) ?? value;
    if (Array.isArray(value)) return value.map(expand);
    if (value === null || typeof value !== "object") return value;

    const result: Record<string, JsonValue> = {};
    for (const [key, child] of Object.entries(value)) {
      const literalKey = quotedValues.get(key);
      const segments =
        literalKey !== undefined
          ? [literalKey]
          : FOLDED_PATH_PATTERN.test(key)
            ? key.split(".")
            : [key];
      let nested = expand(child);
      for (const segment of segments.toReversed()) {
        // __proto__ 等も own property として生成し、prototype chain に書き込まない。
        nested = Object.fromEntries([[segment, nested]]);
      }
      mergeExpandedPaths(result, nested as Record<string, JsonValue>);
    }
    return result;
  }

  const decoded = decode(protectedInput);
  if (!isJsonValue(decoded)) {
    throw new TypeError("Decoded legacy TOON is not valid JSON");
  }
  return expand(decoded);
}

function mergeExpandedPaths(
  target: Record<string, JsonValue>,
  source: Record<string, JsonValue>,
): void {
  for (const [key, value] of Object.entries(source)) {
    if (!Object.hasOwn(target, key)) {
      Object.defineProperty(target, key, {
        value,
        enumerable: true,
        configurable: true,
        writable: true,
      });
      continue;
    }
    const existing = target[key];
    if (
      existing !== null &&
      typeof existing === "object" &&
      !Array.isArray(existing) &&
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      mergeExpandedPaths(existing, value);
    } else {
      throw new TypeError(`Conflicting legacy TOON path: ${key}`);
    }
  }
}
